import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { useShop } from "../contexts/ShopContext";
import SEO from "../components/SEO";
import api from "../api";
import { useI18n } from "../contexts/I18nContext";

/* ── helpers ─────────────────────────────────────────── */
const name = (p, fallback) =>
  p.name || p.model_name || `${p.brand_name || ""} ${p.model_name || ""}`.trim() || fallback || "Product";
const img = (p) => p.image_url || null;
const price = (p) => p.price || p.rental_price || null;
const link = (p) => (p.slug ? `/p/${p.slug}` : "/store");

const SPEC_ROWS = [
  { key: "_type", labelKey: "compare_specType", icon: "tag" },
  { key: "_price", labelKey: "compare_specPrice", icon: "won" },
  { key: "brand_name", labelKey: "compare_specBrand", icon: "building" },
  { key: "color_type", labelKey: "compare_specColor", icon: "palette" },
  { key: "print_speed", labelKey: "compare_specSpeed", icon: "speed" },
  { key: "monthly_volume", labelKey: "compare_specVolume", icon: "pages" },
  { key: "paper_size", labelKey: "compare_specPaper", icon: "paper" },
  { key: "duplex", labelKey: "compare_specDuplex", icon: "duplex" },
  { key: "scan", labelKey: "compare_specScan", icon: "scan" },
  { key: "fax", labelKey: "compare_specFax", icon: "fax" },
  { key: "network", labelKey: "compare_specNetwork", icon: "wifi" },
  { key: "description", labelKey: "compare_specDesc", icon: "info" },
];

function specVal(item, key, tr) {
  if (key === "_type") {
    const tp = item._product_type;
    return tp === "sktec" ? tr.compare_sktec : tp === "ikoffices" ? tr.compare_rental : tp === "copier" ? tr.compare_copier : tr.compare_toner;
  }
  if (key === "_price") {
    const p = price(item);
    return p ? `${Number(p).toLocaleString()}${tr.compare_won}` : "-";
  }
  if (key === "description") {
    const v = item.description || item.memo || "-";
    return v.length > 60 ? v.slice(0, 60) + "…" : v;
  }
  return item[key] || "-";
}

/* ── SVG icons ───────────────────────────────────────── */
const CompareIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
  </svg>
);
const PlusIcon = ({ className = "w-8 h-8" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);
const CartIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
  </svg>
);
const XIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const SearchIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
  </svg>
);
const ChevronIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
  </svg>
);
const PrinterIcon = ({ className = "w-12 h-12" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m0 0a48.44 48.44 0 0110.5 0m-10.5 0V4.875c0-.621.504-1.125 1.125-1.125h8.25c.621 0 1.125.504 1.125 1.125v2.234" />
  </svg>
);

/* ── Product selector dropdown ───────────────────────── */
function ProductSelector({ onSelect, excludeIds, allProducts }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handle = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return allProducts
      .filter((p) => !excludeIds.has(`${p._product_type}_${p._product_id}`))
      .filter((p) => !q || name(p).toLowerCase().includes(q) || (p.brand_name || "").toLowerCase().includes(q) || (p.category || "").toLowerCase().includes(q))
      .slice(0, 50);
  }, [allProducts, excludeIds, query]);

  return (
    <div ref={ref} className="relative w-full">
      <button
        onClick={() => setOpen(!open)}
        className="w-full h-full min-h-[140px] sm:min-h-[280px] border-2 border-dashed border-gray-200 rounded-2xl flex flex-col items-center justify-center gap-3 text-gray-400 hover:border-[#0071e3] hover:text-[#0071e3] transition-all group bg-white/50"
      >
        <div className="w-14 h-14 rounded-full bg-gray-50 group-hover:bg-blue-50 flex items-center justify-center transition-colors">
          <PlusIcon className="w-7 h-7" />
        </div>
        <span className="text-sm font-medium">{t.compare_addProduct}</span>
      </button>

      {open && (
        <div className="absolute z-50 top-0 left-0 right-0 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden max-h-[420px] flex flex-col">
          <div className="p-3 border-b border-gray-100 flex items-center gap-2">
            <SearchIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input
              type="text"
              placeholder={t.compare_searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full text-sm outline-none placeholder-gray-400"
              autoFocus
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">{t.compare_noSearchResults}</div>
            ) : (
              filtered.map((p) => (
                <button
                  key={`${p._product_type}_${p._product_id}`}
                  onClick={() => { onSelect(p); setOpen(false); setQuery(""); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition text-left"
                >
                  {img(p) ? (
                    <img src={img(p)} alt="" className="w-10 h-10 rounded-lg object-contain bg-gray-50 flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <PrinterIcon className="w-5 h-5 text-gray-300" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{name(p, t.compare_product)}</p>
                    <p className="text-xs text-gray-500">
                      {p._product_type === "sktec" ? t.compare_sktec : t.compare_rental}
                      {p.brand_name ? ` · ${p.brand_name}` : ""}
                      {price(p) ? ` · ${Number(price(p)).toLocaleString()}${t.compare_won}` : ""}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Main ComparePage ────────────────────────────────── */
export default function ComparePage() {
  const { t } = useI18n();
  const { compareItems, removeFromCompare, clearCompare, addToCart, addToCompare } = useShop();
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Fetch all products for the dropdown selector
  useEffect(() => {
    Promise.all([
      api.get("/api/sktec-products").catch(() => ({ data: [] })),
      api.get("/api/ikoffices-products").catch(() => ({ data: [] })),
    ]).then(([s, i]) => {
      const sktec = s.data.map((p) => ({ ...p, _product_type: "sktec", _product_id: p.id }));
      const ik = i.data.map((p) => ({ ...p, _product_type: "ikoffices", _product_id: p.id }));
      setAllProducts([...sktec, ...ik]);
      setLoading(false);
    });
  }, []);

  const excludeIds = useMemo(
    () => new Set(compareItems.map((p) => `${p._product_type}_${p._product_id}`)),
    [compareItems]
  );

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2000);
  };

  const handleAddToCart = async (item) => {
    const result = await addToCart(item._product_type, item._product_id);
    if (result?.needLogin) showToast(t.compare_loginRequired);
    else showToast(t.compare_addedToCart);
  };

  const handleSelect = (product) => {
    addToCompare(product);
  };

  const emptySlots = compareItems.length === 0 ? 3 : 1;

  return (
    <div className="bg-[#fbfbfd] min-h-screen">
      <SEO title={t.compare_heading} path="/compare" />

      {/* Toast */}
      {toast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white text-sm px-5 py-2.5 rounded-full shadow-lg animate-fade-in">
          {toast}
        </div>
      )}

      {/* Hero banner */}
      <section className="relative bg-gradient-to-b from-[#0a0a12] to-[#1a1a2e] text-white min-h-[380px] flex items-center">
        <div className="max-w-5xl mx-auto px-6 pt-24 pb-16 text-center w-full">
          <h1 className="text-3xl md:text-[44px] font-bold tracking-tight leading-tight">{t.compare_heading}</h1>
          <p className="text-[16px] text-white/70 mt-3 max-w-xl mx-auto">{t.compare_subtitle}</p>
        </div>
      </section>

      {/* Toolbar */}
      {compareItems.length > 0 && (
        <div className="max-w-[1400px] mx-auto px-4 mb-4 flex items-center justify-between">
          <span className="text-sm text-gray-500">{compareItems.length}{t.compare_comparing}</span>
          <button
            onClick={clearCompare}
            className="text-sm text-gray-500 hover:text-red-500 transition flex items-center gap-1"
          >
            <XIcon className="w-3.5 h-3.5" />
            {t.compare_clearAll}
          </button>
        </div>
      )}

      {/* Product columns (sticky area) */}
      <div className="max-w-[1400px] mx-auto px-4">
        <div
          className="grid gap-4 justify-center"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 260px))" }}
        >
          {/* Filled slots */}
          {compareItems.map((item) => (
            <div key={`${item._product_type}_${item._product_id}`} className="bg-white rounded-2xl p-5 relative group">
              {/* Remove button */}
              <button
                onClick={() => removeFromCompare(item._product_type, item._product_id)}
                className="absolute top-3 right-3 w-7 h-7 rounded-full bg-gray-100 hover:bg-red-50 text-gray-400 hover:text-red-500 flex items-center justify-center transition opacity-0 group-hover:opacity-100"
              >
                <XIcon className="w-3.5 h-3.5" />
              </button>

              {/* Product image */}
              <Link to={link(item)} className="block">
                <div className="w-full aspect-square flex items-center justify-center mb-4 p-4">
                  {img(item) ? (
                    <img src={img(item)} alt={name(item, t.compare_product)} className="max-w-full max-h-full object-contain hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <PrinterIcon className="w-20 h-20 text-gray-200" />
                  )}
                </div>
              </Link>

              {/* Source badge */}
              <div className="mb-2">
                <span className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  item._product_type === "sktec" ? "bg-orange-50 text-orange-600" : "bg-blue-50 text-blue-600"
                }`}>
                  {item._product_type === "sktec" ? t.compare_sktec : t.compare_rental}
                </span>
              </div>

              {/* Product name */}
              <Link to={link(item)}>
                <h3 className="text-sm font-semibold text-gray-900 leading-snug mb-1 line-clamp-2 hover:text-[#0071e3] transition">
                  {name(item, t.compare_product)}
                </h3>
              </Link>

              {/* Price */}
              <p className="text-lg font-bold text-gray-900 mb-4">
                {price(item) ? `${Number(price(item)).toLocaleString()}${t.compare_won}` : <span className="text-gray-400 text-sm font-normal">{t.compare_priceInquiry}</span>}
              </p>

              {/* Action buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleAddToCart(item)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-[#0071e3] text-white text-xs font-medium rounded-xl hover:bg-[#0077ed] transition"
                >
                  <CartIcon className="w-3.5 h-3.5" />
                  {t.compare_cart}
                </button>
              </div>

              {/* Detail link */}
              <Link
                to={link(item)}
                className="block mt-2 text-center text-xs text-[#0071e3] hover:underline"
              >
                {t.compare_detail} &gt;
              </Link>
            </div>
          ))}

          {/* Empty slots with dropdown selector */}
          {Array.from({ length: emptySlots }).map((_, i) => (
            <div key={`empty-${i}`} className="rounded-2xl">
              <ProductSelector
                onSelect={handleSelect}
                excludeIds={excludeIds}
                allProducts={allProducts}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Spec comparison table - Desktop: horizontal grid, Mobile: vertical cards */}
      {compareItems.length > 0 && (
        <div className="max-w-[1400px] mx-auto px-4 mt-10 mb-16">
          {/* Desktop table */}
          <div className="hidden sm:block bg-white rounded-2xl overflow-x-auto">
            {SPEC_ROWS.map((row, idx) => (
              <div
                key={row.key}
                className={`grid items-start ${idx !== 0 ? "border-t border-gray-100" : ""}`}
                style={{ gridTemplateColumns: `160px repeat(${compareItems.length}, minmax(150px, 1fr))` }}
              >
                <div className="px-6 py-4 flex items-center gap-2">
                  <span className="text-[13px] font-semibold text-gray-500">{t[row.labelKey]}</span>
                </div>
                {compareItems.map((item) => {
                  const val = specVal(item, row.key, t);
                  const isHighlight = row.key === "_price" && val !== "-";
                  return (
                    <div key={`${item._product_type}_${item._product_id}_${row.key}`} className="px-5 py-4">
                      <span className={`text-sm ${isHighlight ? "font-semibold text-gray-900" : "text-gray-700"}`}>{val}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          {/* Mobile: vertical card per product */}
          <div className="sm:hidden space-y-4">
            {compareItems.map((item) => (
              <div key={`mobile-spec-${item._product_type}_${item._product_id}`} className="bg-white rounded-2xl overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                  <span className="text-[13px] font-semibold text-gray-900">{name(item, t.compare_product)}</span>
                </div>
                {SPEC_ROWS.map((row, idx) => {
                  const val = specVal(item, row.key, t);
                  const isHighlight = row.key === "_price" && val !== "-";
                  return (
                    <div key={row.key} className={`px-4 py-2.5 ${idx !== 0 ? "border-t border-gray-50" : ""}`}>
                      <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">{t[row.labelKey]}</span>
                      <p className={`text-[13px] mt-0.5 ${isHighlight ? "font-semibold text-gray-900" : "text-gray-700"}`}>{val}</p>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {compareItems.length === 0 && !loading && (
        <div className="max-w-[1400px] mx-auto px-4 mt-8 mb-16 text-center">
          <div className="bg-white rounded-2xl py-16 px-8">
            <CompareIcon className="w-16 h-16 text-gray-200 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t.compare_emptyTitle}</h2>
            <p className="text-gray-500 mb-6">{t.compare_emptyDesc}</p>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#0071e3] text-white text-sm font-medium rounded-full hover:bg-[#0077ed] transition"
            >
              {t.compare_browseProd}
            </Link>
          </div>
        </div>
      )}

      {/* CSS for toast animation */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translate(-50%, -8px); } to { opacity: 1; transform: translate(-50%, 0); } }
        .animate-fade-in { animation: fadeIn 0.2s ease-out; }
      `}</style>
    </div>
  );
}
